import React, { useState } from 'react';
import { SectionHeading } from '../ui/SectionHeading';
import { Button } from '../ui/Button';
import { CheckCircle2Icon, UploadIcon } from 'lucide-react';
import { useLang } from '../../lib/i18n/LanguageContext';
import { dictionary } from '../../lib/i18n/dictionary';
const empty = {
  company_name: '',
  website: '',
  contact_person: '',
  email: '',
  phone: '',
  hq_state: '',
  category: '',
  product_description: '',
  certifications: [] as string[],
  exports_internationally: '',
  sold_latam: '',
  spanish_support: '',
  open_to_venezuela: '',
  company_type: '',
  notes: '',
  consent: false
};
const labelCls = 'block text-[13px] font-medium text-navy-deep mb-1.5';
const fieldCls = 'w-full h-11 px-3.5 bg-white border border-slate-200 rounded-sm text-[14px] focus:outline-none focus:border-navy-deep transition-colors';
const textareaCls = 'w-full px-3.5 py-3 bg-white border border-slate-200 rounded-sm text-[14px] focus:outline-none focus:border-navy-deep transition-colors resize-none';
export function SupplierApplicationForm() {
  const {
    lang
  } = useLang();
  const t = dictionary.supplierForm;
  const c = dictionary.common;
  const [form, setForm] = useState(empty);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const update = (k: keyof typeof empty, v: any) => setForm(p => ({
    ...p,
    [k]: v
  }));
  const toggleCert = (cert: string) => setForm(p => ({
    ...p,
    certifications: p.certifications.includes(cert) ? p.certifications.filter(x => x !== cert) : [...p.certifications, cert]
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.company_name) errs.company_name = c.required[lang];
    if (!form.email) errs.email = c.required[lang];
    if (!form.contact_person) errs.contact_person = c.required[lang];
    if (!form.consent) errs.consent = c.required[lang];
    setErrors(errs);
    if (Object.keys(errs).length) return;
    // TODO: Connect to Supabase / Airtable / GoHighLevel / HubSpot / Google Sheets — POST payload here
    console.log('[SupplierApplication] payload', form);
    setSubmitted(true);
  };
  const yesNoInProgress = [c.yes[lang], c.no[lang], c.inProgress[lang]];
  const yesNoNeedInfo = [c.yes[lang], c.no[lang], c.needsInfo[lang]];
  const yesNoPartner = [c.yes[lang], c.no[lang], c.throughPartner[lang]];
  return <section id="supplier-form" className="py-24 md:py-32 bg-slate-50" data-id="element-491">
      <div className="max-w-4xl mx-auto px-5 md:px-8" data-id="element-492">
        <SectionHeading eyebrow={t.eyebrow[lang]} title={t.title[lang]} subtitle={t.subtitle[lang]} data-id="element-493" />

        <div className="mt-10 border border-slate-200 rounded-md bg-white p-6 md:p-10" data-id="element-494">
          {submitted ? <div className="text-center py-12" data-id="element-495">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-full mb-5" data-id="element-496">
                <CheckCircle2Icon className="w-6 h-6 text-emerald-600" data-id="element-497" />
              </div>
              <h3 className="font-serif text-2xl text-navy-deep mb-3" data-id="element-498">
                {t.successTitle[lang]}
              </h3>
              <p className="text-slate-600 max-w-xl mx-auto leading-relaxed" data-id="element-499">
                {t.successBody[lang]}
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-500">
              <div className="grid md:grid-cols-2 gap-5" data-id="element-501">
                <div data-id="element-502">
                  <label className={labelCls} data-id="element-503">
                    {t.fields.companyName[lang]} *
                  </label>
                  <input className={fieldCls} value={form.company_name} onChange={e => update('company_name', e.target.value)} data-id="element-504" />
                  {errors.company_name && <p className="text-xs text-red-600 mt-1" data-id="element-505">
                      {errors.company_name}
                    </p>}
                </div>
                <div data-id="element-506">
                  <label className={labelCls} data-id="element-507">{t.fields.website[lang]}</label>
                  <input className={fieldCls} value={form.website} onChange={e => update('website', e.target.value)} data-id="element-508" />
                </div>
                <div data-id="element-509">
                  <label className={labelCls} data-id="element-510">{t.fields.contact[lang]} *</label>
                  <input className={fieldCls} value={form.contact_person} onChange={e => update('contact_person', e.target.value)} data-id="element-511" />
                  {errors.contact_person && <p className="text-xs text-red-600 mt-1" data-id="element-512">
                      {errors.contact_person}
                    </p>}
                </div>
                <div data-id="element-513">
                  <label className={labelCls} data-id="element-514">{t.fields.email[lang]} *</label>
                  <input type="email" className={fieldCls} value={form.email} onChange={e => update('email', e.target.value)} data-id="element-515" />
                  {errors.email && <p className="text-xs text-red-600 mt-1" data-id="element-516">{errors.email}</p>}
                </div>
                <div data-id="element-517">
                  <label className={labelCls} data-id="element-518">{t.fields.phone[lang]}</label>
                  <input className={fieldCls} value={form.phone} onChange={e => update('phone', e.target.value)} data-id="element-519" />
                </div>
                <div data-id="element-520">
                  <label className={labelCls} data-id="element-521">{t.fields.hqState[lang]}</label>
                  <input className={fieldCls} value={form.hq_state} onChange={e => update('hq_state', e.target.value)} data-id="element-522" />
                </div>
                <div className="md:col-span-2" data-id="element-523">
                  <label className={labelCls} data-id="element-524">{t.fields.category[lang]}</label>
                  <input className={fieldCls} value={form.category} onChange={e => update('category', e.target.value)} data-id="element-525" />
                </div>
              </div>

              <div data-id="element-526">
                <label className={labelCls} data-id="element-527">
                  {t.fields.productDescription[lang]}
                </label>
                <textarea rows={4} className={textareaCls} value={form.product_description} onChange={e => update('product_description', e.target.value)} data-id="element-528" />
              </div>

              <div data-id="element-529">
                <label className={labelCls} data-id="element-530">
                  {t.fields.certifications[lang]}
                </label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-id="element-531">
                  {t.certOptions[lang].map(cert => {
                const on = form.certifications.includes(cert);
                return <label key={cert} className={`flex items-center gap-2.5 px-3.5 h-11 border rounded-sm cursor-pointer text-[13px] transition-colors ${on ? 'border-navy-deep bg-navy-deep/5' : 'border-slate-200 bg-white hover:border-navy-deep/40'}`} data-id="element-532">
                        <input type="checkbox" checked={on} onChange={() => toggleCert(cert)} className="accent-navy-deep" data-id="element-533" />
                        {cert}
                      </label>;
              })}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5" data-id="element-534">
                <div data-id="element-535">
                  <label className={labelCls} data-id="element-536">
                    {t.fields.exportsInternationally[lang]}
                  </label>
                  <select className={fieldCls} value={form.exports_internationally} onChange={e => update('exports_internationally', e.target.value)} data-id="element-537">
                    <option value="" data-id="element-538">{c.select[lang]}</option>
                    {yesNoInProgress.map(o => <option key={o} data-id="element-539">{o}</option>)}
                  </select>
                </div>
                <div data-id="element-540">
                  <label className={labelCls} data-id="element-541">{t.fields.soldLatam[lang]}</label>
                  <select className={fieldCls} value={form.sold_latam} onChange={e => update('sold_latam', e.target.value)} data-id="element-542">
                    <option value="" data-id="element-543">{c.select[lang]}</option>
                    <option data-id="element-544">{c.yes[lang]}</option>
                    <option data-id="element-545">{c.no[lang]}</option>
                  </select>
                </div>
                <div data-id="element-546">
                  <label className={labelCls} data-id="element-547">
                    {t.fields.spanishSupport[lang]}
                  </label>
                  <select className={fieldCls} value={form.spanish_support} onChange={e => update('spanish_support', e.target.value)} data-id="element-548">
                    <option value="" data-id="element-549">{c.select[lang]}</option>
                    {yesNoPartner.map(o => <option key={o} data-id="element-550">{o}</option>)}
                  </select>
                </div>
                <div data-id="element-551">
                  <label className={labelCls} data-id="element-552">
                    {t.fields.openToVenezuela[lang]}
                  </label>
                  <select className={fieldCls} value={form.open_to_venezuela} onChange={e => update('open_to_venezuela', e.target.value)} data-id="element-553">
                    <option value="" data-id="element-554">{c.select[lang]}</option>
                    {yesNoNeedInfo.map(o => <option key={o} data-id="element-555">{o}</option>)}
                  </select>
                </div>
              </div>

              <div data-id="element-556">
                <label className={labelCls} data-id="element-557">{t.fields.companyType[lang]}</label>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-id="element-558">
                  {t.companyTypes[lang].map(ct => <label key={ct} className={`flex items-center gap-2.5 px-3.5 h-11 border rounded-sm cursor-pointer text-[13.5px] transition-colors ${form.company_type === ct ? 'border-navy-deep bg-navy-deep/5' : 'border-slate-200 bg-white hover:border-navy-deep/40'}`} data-id="element-559">
                      <input type="radio" name="company_type" checked={form.company_type === ct} onChange={() => update('company_type', ct)} className="accent-navy-deep" data-id="element-560" />
                      {ct}
                    </label>)}
                </div>
              </div>

              <div data-id="element-561">
                <label className={labelCls} data-id="element-562">{t.fields.catalog[lang]}</label>
                <label className="flex items-center gap-3 px-4 h-12 border border-dashed border-slate-300 rounded-sm cursor-pointer bg-white hover:border-navy-deep/40 transition-colors" data-id="element-563">
                  <UploadIcon className="w-4 h-4 text-slate-400" data-id="element-564" />
                  <span className="text-[13.5px] text-slate-500" data-id="element-565">
                    {t.catalogUpload[lang]}
                  </span>
                  <input type="file" className="hidden" data-id="element-566" />
                </label>
              </div>

              <div data-id="element-567">
                <label className={labelCls} data-id="element-568">{t.fields.notes[lang]}</label>
                <textarea rows={3} className={textareaCls} value={form.notes} onChange={e => update('notes', e.target.value)} data-id="element-569" />
              </div>

              <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-sm bg-slate-50" data-id="element-570">
                <input type="checkbox" checked={form.consent} onChange={e => update('consent', e.target.checked)} className="mt-1 accent-navy-deep" data-id="element-571" />
                <span className="text-[12.5px] text-slate-600 leading-relaxed" data-id="element-572">
                  {t.consent[lang]}
                </span>
              </label>
              {errors.consent && <p className="text-xs text-red-600" data-id="element-573">
                  {t.consentRequired[lang]}
                </p>}

              <Button type="submit" variant="gold" size="lg" className="w-full" data-id="element-574">
                {t.submit[lang]}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}